import { useEffect, useMemo, useRef, useState } from "react";

import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import type { FontVariant } from "@similarweb/styles";
import { IconButton, OnOffSwitch, OnOffSwitchSize, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { MAX_TRACKED_DOMAINS } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import {
    INPUT_HEIGHT,
    INPUT_HEIGHT_PX,
    InlineErrorMessage,
    LLMDomainAutocomplete,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMDomainAutocomplete";
import { getDuplicateTrackedDomainIndices } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

import { useSimpleWarningModal } from "./useSimpleWarningModal";

const MAX_VISIBLE_DOMAINS = 3;
const DOMAIN_ROWS_GAP_PX = 12;
const BORDERS_HEIGHT_PX = 2;
const VISIBLE_DOMAINS_HEIGHT_PX =
    MAX_VISIBLE_DOMAINS * INPUT_HEIGHT_PX + (MAX_VISIBLE_DOMAINS - 1) * DOMAIN_ROWS_GAP_PX + BORDERS_HEIGHT_PX;

interface IDomainsSectionProps {
    title: string;
    titleFontVariant?: FontVariant;
    subtitle?: string;
    domains: IWebsiteItem[];
    changeDomains: (domains: IWebsiteItem[]) => void;
    subdomainsProps: {
        enabled: boolean;
        state?: boolean;
        onChange?: (value: boolean) => void;
        showTooltip?: boolean;
    };
    isReadOnly?: boolean;
    autoFocusLast?: boolean;
}

export const DomainsSection = ({
    title,
    titleFontVariant = "subtitle2",
    subtitle,
    domains,
    changeDomains,
    subdomainsProps,
    isReadOnly,
    autoFocusLast,
}: IDomainsSectionProps) => {
    const {
        enabled: enableIncludeSubdomains,
        state: includeSubdomains = false,
        onChange: setIncludeSubdomains,
        showTooltip: showSubdomainsTooltip,
    } = subdomainsProps;

    const changeDomain = (domain: IWebsiteItem, index: number): void => {
        changeDomains([...domains.slice(0, index), domain, ...domains.slice(index + 1)]);
    };

    const removeDomain = (index: number): void => {
        changeDomains([...domains.slice(0, index), ...domains.slice(index + 1)]);
    };

    const duplicateDomainIndexes = useMemo<Set<number>>(() => getDuplicateTrackedDomainIndices(domains), [domains]);
    const canAddDomain = domains.every((domain) => !!domain?.name);
    const [domainIndexToRemove, setDomainIndexToRemove] = useState<number>(-1);
    const [fieldsRemountKey, setFieldsRemountKey] = useState<number>(0);
    const rowRefs = useRef<Record<number, HTMLElement | null>>({});
    const previouslyDuplicatedIndexesRef = useRef<Set<number>>(new Set());
    const previousDomainsLengthRef = useRef<number>(domains.length);

    useEffect(() => {
        const newlyDuplicatedIndexes = [...duplicateDomainIndexes].filter(
            (index) => !previouslyDuplicatedIndexesRef.current.has(index),
        );
        const hasNewlyAppendedDomain = domains.length > previousDomainsLengthRef.current;
        previouslyDuplicatedIndexesRef.current = duplicateDomainIndexes;
        previousDomainsLengthRef.current = domains.length;

        //a newly appended row is always empty, so it can never also be a newly duplicated one
        const indexToScrollIntoView = newlyDuplicatedIndexes.length
            ? Math.max(...newlyDuplicatedIndexes)
            : hasNewlyAppendedDomain
              ? domains.length - 1
              : undefined;

        if (indexToScrollIntoView >= 0) {
            rowRefs.current[indexToScrollIntoView]?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    }, [duplicateDomainIndexes, domains.length]);

    const { openModal: openRemoveDomainModal, modal: removeDomainModal } = useSimpleWarningModal({
        title: translate("global.remove"),
        text: translate("llm.domains_section.remove.text", {
            domain: domains[domainIndexToRemove]?.name ?? "",
        }),
        ctaText: translate("global.remove"),
        resetState: () => {
            setDomainIndexToRemove(-1);
            setFieldsRemountKey((key) => key + 1);
        },
        onSubmit: () => removeDomain(domainIndexToRemove),
    });

    const requestRemoveDomain = (index: number): void => {
        if (!domains[index]?.name) {
            removeDomain(index);
            return;
        }

        setDomainIndexToRemove(index);
        openRemoveDomainModal();
    };

    const renderDomainField = (domain: IWebsiteItem, index: number) => {
        const isRemovable = !isReadOnly && index > 0;
        const duplicateError = duplicateDomainIndexes.has(index) ? translate("llm.setup.error.duplicate-domain") : "";

        return (
            <Flex
                key={`${domain?.id || domain?.name || "new-domain"}-${index}-${fieldsRemountKey}`}
                ref={(el: HTMLElement | null) => {
                    rowRefs.current[index] = el;
                }}
                data-automation="llm-domains-section-row"
                data-automation-index={index}
                alignItems="flex-start"
                gap="8px"
                width="100%"
            >
                <Flex
                    flex="1"
                    minWidth="0"
                    flexDirection="column"
                >
                    <LLMDomainAutocomplete
                        selectedDomain={domain}
                        setSelectedDomain={(newDomain) => (newDomain ? changeDomain(newDomain, index) : undefined)}
                        onCloseClick={isRemovable ? () => requestRemoveDomain(index) : undefined}
                        placeholder={translate("llm.domains_section.field.placeholder")}
                        searchIcon={!domain?.name ? "search" : undefined}
                        displaySelectedDomainIcon
                        usePortalForDropdown
                        isDisabled={isReadOnly || index === 0}
                        autoFocus={autoFocusLast && !domain?.name && !isReadOnly && index > 0}
                        width="100%"
                        height={INPUT_HEIGHT}
                        error={!!duplicateError}
                    />
                    {duplicateError ? (
                        <InlineErrorMessage
                            fontVariant="caption"
                            color={colorsPalettes.red[400]}
                            data-automation={`llm-domains-section-error-${index}`}
                        >
                            {duplicateError}
                        </InlineErrorMessage>
                    ) : null}
                </Flex>
            </Flex>
        );
    };

    return (
        <Flex
            data-automation="llm-domains-section"
            flexDirection="column"
            gap="12px"
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
            >
                <Text
                    data-automation="llm-domains-section-title"
                    fontVariant={titleFontVariant}
                    color={colorsPalettes.carbon[400]}
                >
                    {title}
                </Text>
                {enableIncludeSubdomains ? (
                    <Flex
                        data-automation={`llm-tracker-edit-domains-toggle`}
                        alignItems="center"
                        gap="4px"
                    >
                        <OnOffSwitch
                            size={OnOffSwitchSize.small}
                            isDisabled={isReadOnly}
                            isSelected={includeSubdomains}
                            onClick={() => setIncludeSubdomains?.(!includeSubdomains)}
                        />
                        <Flex
                            alignItems="center"
                            gap="4px"
                        >
                            <Text
                                fontVariant="captionTitle"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("llm.domains_section.subdomains.label")}
                            </Text>
                            {showSubdomainsTooltip ? (
                                <Tooltip content={translate("llm.domains_section.subdomains.tooltip")}>
                                    <Info24px
                                        size="sm"
                                        color={colorsPalettes.carbon[200]}
                                    />
                                </Tooltip>
                            ) : null}
                        </Flex>
                    </Flex>
                ) : null}
            </Flex>
            {subtitle ? (
                <Text
                    data-automation="llm-domains-section-subtitle"
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                >
                    {subtitle}
                </Text>
            ) : null}
            <Flex
                flexDirection="column"
                gap={`${DOMAIN_ROWS_GAP_PX}px`}
                {...(domains.length > MAX_VISIBLE_DOMAINS
                    ? { maxHeight: `${VISIBLE_DOMAINS_HEIGHT_PX}px`, overflow: "auto" }
                    : {})}
            >
                {domains.map(renderDomainField)}
            </Flex>
            {!isReadOnly && domains.length < MAX_TRACKED_DOMAINS ? (
                <Box>
                    <IconButton
                        type="flat"
                        iconName="add"
                        onClick={() => changeDomains([...domains, {} as IWebsiteItem])}
                        isDisabled={!canAddDomain}
                        data-automation="llm-domains-section-add"
                    >
                        {translate("llm.domains_section.add")}
                    </IconButton>
                </Box>
            ) : null}
            {removeDomainModal}
        </Flex>
    );
};
